import { gradientModelManager } from './gradient-model-manager';
function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hSector = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hSector % 2) - 1));
  const m = l - c / 2;

  let r;
  let g;
  let b;

  if (hSector < 1) {
    r = c;
    g = x;
    b = 0;
  } else if (hSector < 2) {
    r = x;
    g = c;
    b = 0;
  } else if (hSector < 3) {
    r = 0;
    g = c;
    b = x;
  } else if (hSector < 4) {
    r = 0;
    g = x;
    b = c;
  } else if (hSector < 5) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  return [r + m, g + m, b + m];
}

function rgbToHsl(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return [0, 0, l];
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h;

  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return [h * 360, s, l];
}

// Computes the gradient color at an arbitrary position between color stops.
// Color stops are stored in HSL, but we convert both end stops to RGB,
// interpolate linearly in RGB space, and then convert the result back to HSL.
// This matches how colors are ultimately rendered (in RGB), and helps keep
// the transition visually smooth.
export function interpolateHslColorAtPosition(position, colorStops) {
  const sorted = [...colorStops].sort(
    (a, b) =>
      gradientModelManager.extractStopPosition(a) - gradientModelManager.extractStopPosition(b)
  );

  if (sorted.length === 0) {
    return { h: 0, s: 0, l: 0, a: 1 };
  }

  if (position <= gradientModelManager.extractStopPosition(sorted[0])) {
    return { ...gradientModelManager.extractStopColor(sorted[0]) };
  }

  const last = sorted[sorted.length - 1];

  if (position >= gradientModelManager.extractStopPosition(last)) {
    return { ...gradientModelManager.extractStopColor(last) };
  }

  let left = sorted[0];
  let right = sorted[1];

  for (let i = 1; i < sorted.length; i += 1) {
    if (gradientModelManager.extractStopPosition(sorted[i]) >= position) {
      left = sorted[i - 1];
      right = sorted[i];
      break;
    }
  }

  const leftPosition = gradientModelManager.extractStopPosition(left);
  const rightPosition = gradientModelManager.extractStopPosition(right);
  const span = rightPosition - leftPosition || 1;
  const t = (position - leftPosition) / span;

  const leftColor = gradientModelManager.extractStopColor(left);
  const rightColor = gradientModelManager.extractStopColor(right);
  const leftAlpha = leftColor.a;
  const rightAlpha = rightColor.a;

  const [lr, lg, lb] = hslToRgb(leftColor.h, leftColor.s, leftColor.l);
  const [rr, rg, rb] = hslToRgb(rightColor.h, rightColor.s, rightColor.l);

  const interpR = lr + t * (rr - lr);
  const interpG = lg + t * (rg - lg);
  const interpB = lb + t * (rb - lb);
  const interpA = leftAlpha + t * (rightAlpha - leftAlpha);

  const [h, s, l] = rgbToHsl(interpR, interpG, interpB);

  return {
    h: Math.round(h * 100) / 100,
    s: Math.round(s * 10000) / 10000,
    l: Math.round(l * 10000) / 10000,
    a: Math.round(interpA * 1000) / 1000,
  };
}
